/**
 * JobSessionSidebar Component
 *
 * Collapsible left sidebar for job session history:
 * - Session list showing time as name
 * - Status dot indicator (green/red/yellow/blue)
 * - Collapse/expand functionality
 */

import React from 'react';

interface Session {
  session_id: string;
  status: string;
  created_at: string;
  error_message?: string;
}

interface JobSessionSidebarProps {
  sessions: Session[];
  activeSessionId: string | null;
  onSessionSelect: (sessionId: string) => void;
  onToggleSidebar: () => void;
  isLoading?: boolean;
}

export const JobSessionSidebar: React.FC<JobSessionSidebarProps> = ({
  sessions,
  activeSessionId,
  onSessionSelect,
  onToggleSidebar,
  isLoading = false,
}) => {
  const formatTime = (dateString: string): string => {
    const date = new Date(dateString);
    const today = new Date();
    const isToday = date.toDateString() === today.toDateString();

    if (isToday) {
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      }) + ' ' + date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    }
  };

  const getStatusDotColor = (status: string): string => {
    switch (status) {
      case 'completed':
        return 'bg-green-500';
      case 'running':
        return 'bg-blue-500';
      case 'failed':
        return 'bg-red-500';
      case 'pending':
        return 'bg-yellow-500';
      default:
        return 'bg-gray-500';
    }
  };

  return (
    <div className="w-64 bg-white flex flex-col h-full overflow-hidden">
      {/* Header with Toggle */}
      <div className="flex-shrink-0 p-3 bg-white">
        <div className="flex gap-2 items-center">
          {/* Sidebar Toggle Button */}
          <button
            onClick={onToggleSidebar}
            className="p-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 flex-shrink-0"
            aria-label="Hide sidebar"
            title="Hide sidebar"
          >
            <svg
              className="w-4 h-4 text-gray-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
            </svg>
          </button>

          <h2 className="text-sm font-semibold text-gray-900 flex-1">
            Sessions ({sessions.length})
          </h2>
        </div>
      </div>

      {/* Session List - Scrollable */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {isLoading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" aria-label="Loading sessions"></div>
          </div>
        ) : sessions.length === 0 ? (
          <div className="p-4 text-center text-sm text-gray-500">
            No sessions yet
          </div>
        ) : (
          <div className="py-2">
            {sessions.map((session, index) => (
              <button
                key={session.session_id}
                onClick={() => onSessionSelect(session.session_id)}
                className={`
                  w-full px-4 py-2.5 text-left hover:bg-gray-100 transition-colors border-l-4
                  ${activeSessionId === session.session_id
                    ? 'bg-blue-50 border-l-blue-500'
                    : 'border-l-transparent'
                  }
                `}
                aria-current={activeSessionId === session.session_id ? 'true' : undefined}
              >
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-gray-900 flex-1">
                    {formatTime(session.created_at)}
                  </p>

                  {/* Status Dot */}
                  <div
                    className={`w-2 h-2 rounded-full flex-shrink-0 ${getStatusDotColor(session.status)} ${
                      session.status === 'running' ? 'animate-pulse' : ''
                    }`}
                    title={session.status.charAt(0).toUpperCase() + session.status.slice(1)}
                    aria-label={`Status: ${session.status}`}
                  />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
